import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import authAxios from "../../authAxios";
import { ImageResizable } from "../../components/elements/ImageResizeable";
import { Alert, Button, EmptyState, Skeleton } from "../../pages/manage/ui";
import { UnblockUserConfirmationModal } from "./components/BlockConfirmationModals";
import { getDisplayName, getFullName } from "../../utils/userDisplayName";

/**
 * Self-contained "Blocked Users" list with inline unblock.
 *
 * Rows sit in the account pages' fill group (pages/account/kit.js
 * SettingsGroup look). Caller is responsible for the surrounding section
 * heading and description.
 */
const BlockedUsersList = ({ onChange, onCountChange }) => {
  const { t } = useTranslation();

  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userToUnblock, setUserToUnblock] = useState(null);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const [error, setError] = useState(null);

  const loadBlockedUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await authAxios.get("/v1/social/getBlockedUsers");
      const list = data?.data || [];
      setBlockedUsers(list);
      if (onCountChange) onCountChange(list.length);
    } catch (e) {
      console.error("Failed to fetch blocked users:", e);
      setError(
        t("blocked_users_load_error", {
          defaultValue: "Could not load your blocked users. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [t, onCountChange]);

  useEffect(() => {
    loadBlockedUsers();
  }, [loadBlockedUsers]);

  const handleConfirmUnblock = async () => {
    if (!userToUnblock) return;
    setIsUnblocking(true);
    try {
      await authAxios.post(`/v1/social/unblockUser/${userToUnblock.id}`);
      setBlockedUsers((prev) => {
        const next = prev.filter((u) => u.id !== userToUnblock.id);
        if (onCountChange) onCountChange(next.length);
        return next;
      });
      setUserToUnblock(null);
      if (onChange) onChange();
    } catch (e) {
      console.error("Failed to unblock user:", e);
    } finally {
      setIsUnblocking(false);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Skeleton className="tw-h-[60px] tw-w-full" />
        <Skeleton className="tw-h-[60px] tw-w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        variant="destructive"
        action={
          <Button variant="light" size="sm" onClick={loadBlockedUsers}>
            {t("retry", { defaultValue: "Retry" })}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (blockedUsers.length === 0) {
    return (
      <div className="tw-rounded-[var(--r-card)] tw-bg-[var(--fill)]">
        <EmptyState
          compact
          icon={ShieldCheck}
          title={t("no_blocked_users", { defaultValue: "No blocked users" })}
          description={t("blocked_users_empty_hint", { defaultValue: "Users you block will appear here." })}
        />
      </div>
    );
  }

  return (
    <>
      <ul className="tw-overflow-hidden tw-rounded-[var(--r-card)] tw-bg-[var(--fill)] [&>*+*]:tw-border-t [&>*+*]:tw-border-[var(--hairline)]">
        {blockedUsers.map((blockedUser) => {
          const name =
            getDisplayName(blockedUser) ||
            getFullName(blockedUser) ||
            blockedUser.username;
          const dateLabel = blockedUser.blocked_at
            ? formatDate(blockedUser.blocked_at)
            : null;
          const subtitleParts = [
            blockedUser.username ? `@${blockedUser.username}` : null,
            dateLabel
              ? t("blocked_on_short", {
                  defaultValue: "Blocked {{date}}",
                  date: dateLabel,
                })
              : null,
          ].filter(Boolean);

          return (
            <li key={blockedUser.id} className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3">
              <span className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-[var(--fill-strong)] tw-text-sm tw-font-semibold tw-text-muted-foreground">
                {blockedUser.image ? (
                  <ImageResizable size="s" src={blockedUser.image} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.75 }} />
                ) : (
                  name?.charAt(0)?.toUpperCase() || "?"
                )}
              </span>
              <span className="tw-min-w-0 tw-flex-1">
                <span className="tw-block tw-truncate tw-text-[15px] tw-font-medium">{name}</span>
                {subtitleParts.length > 0 && (
                  <span className="tw-block tw-truncate tw-text-[13px] tw-text-muted-foreground">{subtitleParts.join(" · ")}</span>
                )}
              </span>
              <Button variant="light" size="sm" onClick={() => setUserToUnblock({ id: blockedUser.id, name })}>
                {t("unblock", { defaultValue: "Unblock" })}
              </Button>
            </li>
          );
        })}
      </ul>

      <UnblockUserConfirmationModal
        open={!!userToUnblock}
        onClose={() => (isUnblocking ? null : setUserToUnblock(null))}
        userName={userToUnblock?.name}
        onConfirm={handleConfirmUnblock}
        isUnblocking={isUnblocking}
      />
    </>
  );
};

export default BlockedUsersList;
